import { format } from 'date-fns';
import i18n, { dateFnsLocale } from '../i18n/i18n';
import { Alert } from 'react-bootstrap';
import { toast } from './toast';
import { getCurrentUser } from "../services/auth";
import { phoneDigitsForLink } from './phone';

//convert object to query string 
//eg: objectToQueryString({ prop:'prop', anotherProp: 'anotherProp' }) = 'prop=prop&anotherProp=anotherProp'
export const objectToQueryString = (obj) => Object.keys(obj).map(key => key + '=' + obj[key]).join('&')

export function debounce(func, milliseconds = 1000) {

  let timer = null;

  return function (...args) {
    const onComplete = () => {
      func.apply(this, args);
      timer = null;
    }

    if (timer) {
      clearTimeout(timer);
    }

    timer = setTimeout(onComplete, milliseconds);
  };
}

export const displayAddress1 = (address) => {
  let _address = []
  address.apartmentNo && _address.push(address.apartmentNo)
  address.floor && _address.push(address.floor)
  address.intercomName && _address.push(address.intercomName)

  return _address.join(', ')
}

// Civic address line — street through country. Kept separate from
// displayAddress1's apartment/floor/intercom details so callers that render
// those as a distinct secondary line (e.g. "street · apt detail") can use
// each piece independently.
// Normalize diacritical marks to their ASCII base characters so map search
// URLs resolve correctly regardless of whether the database entry uses them.
export const stripDiacritics = (str) =>
  str ? str.normalize('NFD').replace(/[̀-ͯ]/g, '') : str

export const getCivicAddress = (address) => {
  if (!address) return ''
  return [address.street, address.city, address.state, address.zipCode, address.country]
    .filter(Boolean).join(', ')
}

// Single source of truth for the FULL client/job address as one string —
// civic address plus apartment/floor/intercom access details — so it always
// reads completely wherever a single address line is shown (Job Card,
// search, Edit Job modal, Client detail).
export const getFullAddress = (address) => {
  if (!address) return ''
  return [getCivicAddress(address), displayAddress1(address)].filter(Boolean).join(' — ')
}

//convert text to title case
//eg: toTitleCase('titleCase') = 'Title Case'
export const toTitleCase = (text) =>
  text.replace(/([A-Z])/g, " $1").replace(/^./, (match) => match.toUpperCase())

export const initColumns = (
  data, // an array of objects
  excludes = [] // object property that you dont want to be included
) => {
  if (data.length) {
    const firstItem = data[0]
    const columns = Object.keys(firstItem).map(prop => {
      return {
        value: prop,
        label: toTitleCase(prop)
      }
    }).filter(prop => !excludes.includes(prop.value))

    return columns
  }
  return []
}

//@params (columns = array of strings)
export const createColumns = (columns) => {
  return columns.map(col =>
    ({ value: col, label: toTitleCase(col) })
  )
}

export const isObjectEmpty = (obj) => {
  for (var prop in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, prop)) {
      return false;
    }
  }
  return JSON.stringify(obj) === JSON.stringify({});
}

export const pesoFormat = (number) => {
  number = number || 0
  return number.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })
}

export const currencyFormat = (amount, currency) => {
  amount = amount || 0
  return currency ? new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount) : amount.toLocaleString();
}

export const rsdFormat = (number, withSymbol = true) => {
  number = number || 0;
  const formatter = new Intl.NumberFormat('sr-RS', {
    style: withSymbol ? 'currency' : undefined,
    currency: withSymbol ? 'RSD' : undefined,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
    minimumIntegerDigits: 1,
    decimalSeparator: ',',
    groupSeparator: '.',
  });
  return formatter.format(number);
};

export const ronFormat = (value, withSymbol = true) => {
    if (value === null || value === undefined) return '';

    const formatted = new Intl.NumberFormat('ro-RO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);

    return withSymbol ? `${formatted} RON` : formatted;
};

export const toLocalDateString = (date, stringformat = 'dd.MM.yyyy HH:mm') => date && format(new Date(date), stringformat, { locale: dateFnsLocale() })

export const toLocalDateObject = (date) => date && new Date(date + 'Z')

export const statusColorCoding = (status) => {
  if (!status) return

  var statusClass = ''
  if (status.toLowerCase() === 'reservation')
    statusClass = 'text-warning'
  if (status.toLowerCase() === 'downpayment' || status.toLowerCase() === 'payment ongoing')
    statusClass = 'text-info'
  if (status.toLowerCase() === 'past due')
    statusClass = 'text-danger'
  if (status.toLowerCase() === 'fully paid')
    statusClass = 'text-success'
  if (status.toLowerCase() === 'terminated')
    statusClass = 'text-muted'

  return (
    <span className={statusClass}>{status}</span>
  )
}

export const statusAlert = (status) => {
  if (!status) return

  var statusClass = ''
  if (status.toLowerCase() === 'reservation')
    statusClass = 'warning'
  if (status.toLowerCase() === 'downpayment' || status.toLowerCase() === 'payment ongoing')
    statusClass = 'info'
  if (status.toLowerCase() === 'past due')
    statusClass = 'danger'
  if (status.toLowerCase() === 'fully paid')
    statusClass = 'success'
  if (status.toLowerCase() === 'terminated')
    statusClass = 'dark'

  return (
    <Alert variant={statusClass} className="mb-0 p-0">
      <h5 className="p-3 m-0">{status.toUpperCase()}</h5>
    </Alert>
  )
}

// remove props from obj1 that is not found in obj2
export const trimProps = (obj1, obj2, excludes = []) => {
  const newObj = { ...obj1 }

  for (let key in newObj) {
    if (!excludes.includes(key) && !(key in obj2))
      delete newObj[key];
  }
  return newObj
}

export const numberWithCommas = (x) => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export const roundDecimal = (value, decimalPlaces) => {
  if (isNaN(value) || value == Number.POSITIVE_INFINITY) return 0.0
  return value.toFixed(decimalPlaces).replace(/[.,]00$/, "");
}

export const roundDecimalWithCommas = (value, decimalPlaces) => {
  return numberWithCommas(roundDecimal(value, decimalPlaces))
}
// round number to two decimals
export const toTwoDecimals = (num) => {
  return +(Math.round(num + "e+2") + "e-2");
}

export const isAdmin = (user) => {
  const currentUser = user ?? getCurrentUser()
  if (!currentUser) return
  const roles = currentUser['activeRoles'].map(item => item.trim())
  return roles.includes('admin')
}

export const isDriver = (user) => {
  const currentUser = user ?? getCurrentUser()
  if (!currentUser) return
  const roles = currentUser['activeRoles'].map(item => item.trim())
  return roles.includes('driver')
}

export const isUserVerified = (user) => {
  const currentUser = user ?? getCurrentUser()
  if (!currentUser) return
  return currentUser.Verified == "True" ? true : false
}

// convert properties of object to local date object
export const toLocalDateObjectProps = (obj, props = []) => {
  const newObj = { ...obj }

  for (const prop of props) {
    newObj[prop] = newObj[prop] && toLocalDateObject(newObj[prop])
  }

  return newObj
}

export const isDataString = (data) => {
  if (typeof data === 'string' || data instanceof String)
    return true
  else
    return false
}

export const getInitials = (text) => {
  if(!text) return ''
  const textSplit = text.split(" ")
  return textSplit.length > 1 ? `${textSplit[0].charAt(0)}${textSplit[1].charAt(0)}` : `${text.charAt(0)}${text.charAt(1)}`
}

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const prettyTitle = (title) => {
  return (capitalizeFirstLetter(title)).replace(/([A-Z])/g, ' $1').trim()
}

export const copyObj = (obj, excludes = []) => {
  const newObj = {}

  for (let key in obj) {
    if (!excludes.includes(key))
      newObj[key] = obj[key]
  }
  return newObj
}

export const hasValue = (val) => {
  return val != null && val != undefined && !isNaN(val)
}
// Digits-only phone in full international form for wa.me / viber:// links
// ("0721 172 247" -> "40721172247"). Those links need the country code and
// no trunk zero; the code comes from the company's settings (utils/phone.js),
// never a hard-coded country — and a number that already carries "+"/"00"
// is left on its own country.
export const intlPhoneDigits = (phone) => phoneDigitsForLink(phone)

// Open a Viber chat for the given phone. viber:// deep links fail SILENTLY
// when the app isn't installed (there is no https fallback like wa.me), so
// if this page is still visible shortly after the attempt, no app claimed
// the link — tell the user instead of appearing to do nothing.
export const openViberChat = (phone) => {
  const digits = intlPhoneDigits(phone)
  if (!digits) return
  window.location.href = `viber://chat/?number=%2B${digits}`
  setTimeout(() => {
    if (!document.hidden) {
      toast.info(i18n.t('misc.viberNotInstalled'), {
        position: toast.POSITION.BOTTOM_RIGHT
      })
    }
  }, 1400)
}
